// liveRunner.ts (GH3) - the browser hand runner + live advisor. Deals a FRESH engine hand and walks it
// street by street, producing one snapshot per hero postflop decision: the felt, the true legal option
// set (via the GH2 adaptor), the engine's recommendation, a per-street grader, and deterministic ($0)
// template coach text. Observe / Diagnose / Solo all bind to this so every entry deals a different hand.
//
// It does NOT re-derive strategy - it composes the SHIPPED engine exactly as the proven pieces do:
//   generate()  (spot-targeted, leak-biasable)  → the first postflop hero decision
//   then a forward walk (villains scripted via scriptVillainAction; advance() runs out the board),
//   and at each hero decision the GH1 live-advice chain:
//     selectVillainStartingRange → narrowThroughStreets → assembleLiveContext → recommend → gradeDecision
//   plus assembleFacts → renderAllTemplates for the coach lines. This is the S17 play-hand pipeline moved
//   into the browser, upgraded from play-hand's coarse per-archetype range (the `:568` simplification) to
//   the GH1 per-street-narrowed range. Preflop is auto-played (preflopOpenCallAutopilot), matching S17;
//   decisions are captured on the flop / turn / river (keyed by their real street index for the stepper).
import type {
  Action,
  ActionKind,
  ArchetypeId,
  CardIndex,
  EngineFacts,
  EquityResult,
  FacingEvent,
  FactsDigest,
  HandState,
  LegalAction,
  PlayerState,
  Position,
  PositionClass,
  PotType,
  PreflopEngineFacts,
  PreflopFactsDigest,
  PreflopRecommendation,
  RecommendedAction,
  ScenarioSpec,
  Street,
} from '@postflop/schema';
import {
  advance,
  assembleFacts,
  assemblePreflopFacts,
  projectPreflopDigest,
  assembleLiveContext,
  classifyCombo,
  classifyTexture,
  createRng,
  dealForSpec,
  decideVillainPreflop,
  generate,
  gradeDecision,
  legalActions,
  narrowThroughStreets,
  postflopOrder,
  preflopOpenCallAutopilot,
  projectDigest,
  recommend,
  scriptVillainAction,
  selectVillainStartingRange,
  sprBucketOf,
  TsEquityProvider,
  type DecisionGrade,
  type GeneratedScenario,
  type PreflopDecisionGrade,
  type PreflopUserDecision,
  type StrategyContext,
  type UserDecision,
  type VillainLineStep,
  type VillainPreflopPolicy,
} from '@postflop/engine';
import { renderAllTemplates, type CoachLine } from '@postflop/coach';
import { archetypeOf, rangeSetOf, RULES } from './liveData';
import { legalToActionRamp, scenarioToFelt, type RampAction } from './liveHand';
import {
  makeHeroPlayedPreflopAutopilot,
  mechanicalPreflopAction,
  resolveHeroFirstPreflop,
  resolveHeroPreflopAt,
  tableTypeFamily,
  type HeroPreflopDecision,
  type PreflopPlayDeps,
} from './preflopHandoff';
import { segmentsFromHandState, type ReplaySegments } from '../replay/segment';
import type { FeltView } from './bridge';

const EQUITY_ITERATIONS = 1500; // browser-friendly MC budget; exact enumeration kicks in when cheap.
const SIZING_FRACTION: Record<string, number> = {
  min: 0.25, third: 1 / 3, half: 0.5, twoThirds: 2 / 3, threeQuarters: 0.75, pot: 1, overbet: 1.4, jam: 1,
};

// ── live-state derivations (ported 1:1 from the S17 harness; the facts layer owns these internally but
//    does not export them, so the runner re-derives them from the same primitives). ────────────────────
const potTotal = (s: HandState): number => s.pots.reduce((sum, p) => sum + p.amount, 0);

const effectiveStackFor = (s: HandState, heroSeat: number): number => {
  const hero = s.players.find((p) => p.seat === heroSeat)!;
  const others = s.players.filter((p) => p.seat !== heroSeat && !p.folded);
  const maxOther = others.length ? Math.max(...others.map((p) => p.stack)) : 0;
  return Math.min(hero.stack, maxOther);
};

const potTypeOf = (s: HandState): PotType => {
  const bb = s.spec.stakes.bb;
  const raises = s.history.filter(
    (h) => h.street === 'preflop' && (h.action.kind === 'raise' || (h.action.kind === 'jam' && (h.action.amount ?? 0) > bb)),
  ).length;
  return raises === 0 ? 'limped' : raises === 1 ? 'srp' : raises === 2 ? '3bp' : '4bp';
};

const positionClassOf = (s: HandState, heroSeat: number): PositionClass => {
  const order = postflopOrder(s.players.length, s.buttonSeat);
  const live = order.filter((seat) => {
    const p = s.players.find((pp) => pp.seat === seat);
    return p && !p.folded;
  });
  return live[live.length - 1] === heroSeat ? 'IP' : 'OOP';
};

const facingOf = (s: HandState, hero: PlayerState): FacingEvent => {
  const toCall = s.currentBet - hero.committedThisStreet;
  if (toCall <= 0) return 'check';
  if (s.street === 'preflop') {
    const raises = s.history.filter((h) => h.street === 'preflop' && h.action.kind === 'raise').length;
    return raises >= 3 ? '4bet' : raises === 2 ? '3bet' : 'raise';
  }
  const pot = potTotal(s);
  return toCall / Math.max(pot, 1) > 0.66 ? 'bet-large' : 'bet-small';
};

// ── the villain's postflop line → the narrow() steps, so the range tightens against what he actually did.
const STREET_ORDER: Street[] = ['preflop', 'flop', 'turn', 'river'];

/** The one representative action the villain took on `street` (most aggressive wins), mapped onto a
 *  VillainLineStep the narrow() engine consumes. Null when the villain did nothing that street. */
const villainStepForStreet = (s: HandState, villainSeat: number, street: Street): VillainLineStep | null => {
  const acts = s.history.filter((h) => h.street === street && h.action.seat === villainSeat).map((h) => h.action);
  if (acts.length === 0) return null;
  const rank = (k: string): number => (k === 'jam' ? 4 : k === 'raise' ? 3 : k === 'bet' ? 2 : k === 'call' ? 1 : 0);
  const top = acts.reduce((best, a) => (rank(a.kind) > rank(best.kind) ? a : best), acts[0]);
  if (top.kind === 'bet' || top.kind === 'jam') return { street, facing: 'bet-large', responded: 'bet' };
  if (top.kind === 'raise') return { street, facing: 'raise', responded: 'raise' };
  if (top.kind === 'call') return { street, facing: 'bet-small', responded: 'call' };
  return { street, facing: 'check', responded: 'check' };
};

/** The villain's line on every completed street strictly before `current` (preflop is pre-encoded in the
 *  starting range, so it is skipped by narrowThroughStreets anyway). */
const villainLineBefore = (s: HandState, villainSeat: number, current: Street): VillainLineStep[] => {
  const idx = STREET_ORDER.indexOf(current);
  const steps: VillainLineStep[] = [];
  for (let i = 1; i < idx; i++) {
    const step = villainStepForStreet(s, villainSeat, STREET_ORDER[i]);
    if (step) steps.push(step);
  }
  return steps;
};

// ── the GH1 live-advice chain for one hero decision. Returns the context, the engine's recommendation,
//    and the equity result (reused for the facts layer). ────────────────────────────────────────────────
interface LiveAdvice {
  ctx: StrategyContext;
  rec: RecommendedAction;
  equity: EquityResult;
  villainArchetypeId: ArchetypeId;
}

const equityProvider = new TsEquityProvider(createRng('gh3-live-equity'));

const buildAdvice = (
  s: HandState,
  heroCards: readonly [CardIndex, CardIndex],
  villain: PlayerState,
): LiveAdvice => {
  const archId = villain.archetypeId!;
  const arch = archetypeOf(archId);
  const board = s.board;
  // Dead cards MUST include the board, not just hero's holes: selectVillainStartingRange only removes
  // what's in `dead`, and narrowThroughStreets only removes board cards up to the last-narrowed street
  // (flop/turn) - never the river card. Omitting the board leaves board-colliding villain combos (e.g.
  // QhQd on a board containing Qd) in the range, which then feed a duplicate-card 7-card hand into the
  // evaluator and crash it ("C is not iterable" from @poker-apprentice describeHand). Including the full
  // current board here zeroes those combos at the source, for every street.
  const dead = [...heroCards, ...board] as CardIndex[];

  // (1) the villain's PREFLOP starting range off his real preflop line, then (2) tighten it through the
  // streets he has already acted on. Preflop is pre-encoded in the starting range.
  const rangeSet = rangeSetOf(arch.preflop.rangeSetId);
  const selected = selectVillainStartingRange(rangeSet, s, villain.seat, { dead });
  const steps = villainLineBefore(s, villain.seat, s.street);
  const continuing =
    steps.length > 0
      ? narrowThroughStreets({ startingRange: selected.range, arch, steps, board, dead }).range
      : selected.range;

  const { handClass, drawFlags } = classifyCombo(heroCards, board);
  const pot = potTotal(s);
  const toCall = Math.max(0, s.currentBet - s.players.find((p) => p.seat === s.spec.heroSeat)!.committedThisStreet);
  const spr = pot > 0 ? effectiveStackFor(s, s.spec.heroSeat) / pot : 0;

  // (3) assemble the context - hero equity + villain shape are DERIVED from the narrowed range, not faked.
  const ctx = assembleLiveContext({
    street: s.street,
    facing: facingOf(s, s.players.find((p) => p.seat === s.spec.heroSeat)!),
    positionClass: positionClassOf(s, s.spec.heroSeat),
    potType: potTypeOf(s),
    playersInHand: s.players.filter((p) => !p.folded).length,
    spr,
    sprBucket: sprBucketOf(spr),
    textureClass: board.length >= 3 ? classifyTexture(board).textureClass : 'dry',
    price: { pot, toCall },
    hero: { cards: heroCards, handClass, drawFlags },
    board,
    villain: { archetype: arch, range: continuing },
    equity: equityProvider,
    rules: RULES,
    equityIterations: EQUITY_ITERATIONS,
  });

  // (4) the engine's recommendation for this exact spot (the S7 heart).
  const rec = recommend(ctx);
  return { ctx, rec, equity: { equity: ctx.hero.equity, ci95: 0 }, villainArchetypeId: archId };
};

// ── amount helpers (ported from the S17 harness) - drive a sizing intent to a concrete legal "to" amount.
const amountForIntent = (user: UserDecision, pot: number, currentBet: number, legal: LegalAction[]): number | undefined => {
  if (user.action !== 'bet' && user.action !== 'raise') return undefined;
  const l = legal.find((x) => x.kind === user.action);
  const frac = user.sizingIntent ? SIZING_FRACTION[user.sizingIntent] ?? 0.66 : 0.66;
  const raw = user.action === 'bet' ? frac * pot : currentBet + frac * pot;
  let to = Math.round(raw);
  if (l?.min !== undefined) to = Math.max(to, l.min);
  if (l?.max !== undefined) to = Math.min(to, l.max);
  return to;
};

/** Map the strategy layer's recommended action onto the TRUE legal set. recommend() speaks a strategic
 *  vocabulary ('check' = the passive line, 'bet' = aggression) that can name an action the current state
 *  does not literally offer - e.g. it returns 'check' when hero is facing a bet (legal: fold/call/raise),
 *  where the passive line IS a call. The S17 harness works around this with a legal.find(...) ?? fallback;
 *  we make the mapping explicit so the "book line" text + the Observe pro's decision are always coherent. */
const legalizeAction = (action: ActionKind, legal: LegalAction[]): ActionKind => {
  const kinds = new Set(legal.map((l) => l.kind));
  if (kinds.has(action)) return action;
  const pick = (...cands: ActionKind[]): ActionKind => cands.find((c) => kinds.has(c)) ?? legal[0].kind;
  switch (action) {
    case 'check': return pick('call', 'check', 'fold');
    case 'call': return pick('check', 'call', 'fold');
    case 'bet': return pick('raise', 'bet', 'call', 'check');
    case 'raise': return pick('raise', 'bet', 'call');
    case 'jam': return pick('jam', 'raise', 'bet', 'call');
    case 'fold': return pick('fold', 'check', 'call');
  }
};

const foldProbFor = (villain: PlayerState): number => {
  const vol = archetypeOf(villain.archetypeId!).imbalances.bettingVolume;
  return vol === 'overCalls' ? 0.08 : vol === 'overFolds' ? 0.5 : 0.28;
};

const heroPerceivedFrom = (s: HandState, hero: PlayerState): EngineFacts['heroPerceived'] => {
  const hasBet = s.history.some((h) => h.action.seat === hero.seat && (h.action.kind === 'bet' || h.action.kind === 'raise'));
  return { summary: [hasBet ? 'a value-leaning betting range' : 'a positional calling range'], capped: false };
};

// ── the snapshot a UI surface renders + drives for one hero decision. ─────────────────────────────────
export interface LiveDecisionResult {
  grade: DecisionGrade;
  coach: CoachLine[];
  facts: EngineFacts;
}

export interface PostflopSnapshot {
  /** discriminant for the widened LiveSnapshot union (PF3): a postflop hero decision. */
  kind: 'postflop';
  /** the real street index (0..3) - flop=1, turn=2, river=3 - so the 4-pip StreetStepper reads right. */
  streetIndex: number;
  street: Street;
  /** the raw engine HandState at this hero decision - the re-advance point for the GH6 counterfactual
   *  ("what would've happened down the recommended line"). UI reads the felt/ramp; buildCounterfactual
   *  reads this to replay the correct line to showdown. */
  state: HandState;
  felt: FeltView;
  legal: LegalAction[];
  ramp: RampAction[];
  price: { pot: number; toCall: number };
  recommendation: RecommendedAction;
  /** the recommendation's action mapped onto the TRUE legal set (for the "book line" display + Observe's
   *  pro decision + the matched-book check). raw `recommendation.finalAction` stays for the EV grader. */
  bookAction: ActionKind;
  villainArchetypeId: ArchetypeId;
  /** TB2: the SEAT the read is about. At the 3-handed baseSpec the archetype identified the opponent on
   *  its own; at a 7-handed table two seats can share an archetype, and the persistent per-seat read
   *  (TB3) keys on the seat, so the snapshot has to name it. */
  villainSeat: number;
  heroEquityPct: number;
  /** the engine-line coach text for this spot ($0 deterministic templates). */
  coach: CoachLine[];
  /** GH5: the card-free FactsDigest for this spot - the ONLY thing handed to the narration lane
   *  (/api/coach). Projected from the reference facts; carries no hole cards / board / dollars. */
  digest: FactsDigest;
  /** grade the user's actual choice against the recommendation (outcome-independent). */
  grade: (user: UserDecision) => LiveDecisionResult;
}

/** PF3 step 0: hero's REAL played preflop decision - the playable analogue of GH8's Observe recap. It is
 *  boardless (no felt board, no textureClass, no postflop hand class) and carries the preflop-typed
 *  recommendation + the boardless facts/digest. The DO-mode stepper opens on this before the postflop walk.
 *  PF4 renders the preflop ramp over it; PF3 builds + proves the model seam. */
export interface PreflopSnapshot {
  kind: 'preflop';
  streetIndex: 0;
  street: 'preflop';
  /** the HandState at hero's first preflop decision (before hero acts). */
  state: HandState;
  /** boardless felt (no community cards yet). */
  felt: FeltView;
  legal: LegalAction[];
  ramp: RampAction[];
  price: { pot: number; toCall: number };
  /** the PREFLOP-typed recommendation (intent: limp/open/3bet/…, chart provenance) - NOT a postflop one. */
  recommendation: PreflopRecommendation;
  villainArchetypeId: ArchetypeId;
  /** TB5: the SEAT the read at THIS node is about - the villain hero faces here (the opener at his first
   *  decision, the 4-bettor at a node where he 3-bet and got re-raised). Mirrors PostflopSnapshot.villainSeat.
   *  The per-node read (question + Axis-B grade + persisted tag) all key on this, so a hand where the opener
   *  is not the node villain no longer tags three different men. */
  villainSeat: number;
  /** the boardless preflop facts (full record) + its card-free coach digest. */
  facts: PreflopEngineFacts;
  digest: PreflopFactsDigest;
  /** grade hero's chosen preflop intent against the recommendation (PF2, outcome-independent). */
  grade: (user: PreflopUserDecision) => PreflopDecisionGrade;
}

/** The widened DO-mode step: a preflop decision OR a postflop decision, discriminated on `kind`/`street`.
 *  The stepper list opens on a PreflopSnapshot and continues into PostflopSnapshots. */
export type LiveSnapshot = PreflopSnapshot | PostflopSnapshot;

export interface LiveHandModel {
  handId: string;
  seed: string;
  spec: ScenarioSpec;
  heroSeat: number;
  heroCards: readonly [CardIndex, CardIndex];
  villainSeat: number;
  villainArchetypeId: ArchetypeId;
  villainName: string;
  /** postflop hero decisions, in street order (flop → turn → river). Empty is impossible for a returned
   *  hand. Unchanged by PF3 - the existing surfaces bind to these; PF4 adds the preflop step. */
  snapshots: PostflopSnapshot[];
  /**
   * FULL (re-opened): EVERY preflop decision hero faces, in order - not just his first.
   *
   * PF3 modeled preflop as one node because the demo path only ever showed one. A real hand asks again the
   * moment hero re-raises and a villain comes back over the top, and those nodes (DEFEND / 3-bet / 4-bet)
   * are exactly the ones TB0 unlocked by omitting `heroPosition` and PF4 built + unit-proved on synthetic
   * snapshots. They were reachable at the table but not playable until they became steps of their own.
   *
   * Empty on the default (Observe/GH) path, which does not play the preflop.
   */
  preflops: PreflopSnapshot[];
  /** PF3: hero's FIRST preflop decision - `preflops[0]`, kept as its own field because that is the node the
   *  preflop recap, the felt fallback and the wrong-fold counterfactual all mean by "the preflop". */
  preflop?: PreflopSnapshot;
  /** PF3: the DO-mode stepper list - `[...preflops, ...snapshots]`. On the default (Observe/GH) path this
   *  equals `snapshots`. */
  steps: LiveSnapshot[];
  /** the terminal state this hand walked to. REAL for every decision hero has actually played, PROVISIONAL
   *  (the pro's line) for any he has not reached yet - so it is only the truth of the table once the hand is
   *  `done`. That is exactly when the session is allowed to harvest it (`completeHand`). */
  terminal: HandState;
  heroNetChips: number;
  /** FULL - the dealt, unplayed state this hand started from. Kept so `applyHeroDecision` can replay the
   *  hand deterministically around a real decision; without it the model cannot be rebuilt from its seed. */
  initial: HandState;
  /** FULL - what hero has really played so far (`applyHeroDecision` appends to it). */
  heroLine: HeroLine;
  /** FULL - the deal options this hand was dealt with, so a rebuild reproduces it exactly. */
  opts: DealSessionOptions;
  /** FULL - has hero finished deciding? True once the hand reaches a terminal with no unplayed hero
   *  decision left, which is the only moment `terminal` is honest and the session may harvest. */
  done: boolean;
}

/** Friendly villain names over the archetype ids (the GH2-documented GH3 display seam). Reuses the
 *  authored nicknames where they exist (Vacuum/Firecracker/Rock/Whale) and names the rest in the same key. */
const VILLAIN_DISPLAY: Partial<Record<ArchetypeId, string>> = {
  sticker: 'The Vacuum',
  firestarter: 'The Firecracker',
  rock: 'The Rock',
  tourist: 'The Whale',
  sheriff: 'The Sheriff',
  bully: 'The Bully',
  professor: 'The Professor',
  surgeon: 'The Surgeon',
  volcano: 'The Volcano',
  waitingRoom: 'The Waiting Room',
  taxCollector: 'The Tax Collector',
  scaredStack: 'The Scared Stack',
  promoChaser: 'The Promo Chaser',
  tableCaptain: 'The Table Captain',
  shortStackGambler: 'The Gambler',
};
export const displayName = (id: ArchetypeId): string => VILLAIN_DISPLAY[id] ?? id;

/** Wrap a walk state into the minimal GeneratedScenario shape scenarioToFelt reads (reuses the GH2 felt
 *  builder untouched - it only ever reads decisionPoint.state + decisionPoint.pot). */
const feltFor = (state: HandState): FeltView => {
  const scenario = { decisionPoint: { state, pot: potTotal(state) } } as unknown as GeneratedScenario;
  const felt = scenarioToFelt(scenario);
  // map archetype ids to friendly display names on the opponent pods (GH3 display seam).
  return {
    ...felt,
    seats: felt.seats.map((seat) =>
      seat.name in ARCH_NAME_LOOKUP
        ? { ...seat, name: podName(ARCH_NAME_LOOKUP[seat.name]), avatar: podName(ARCH_NAME_LOOKUP[seat.name]).charAt(0) }
        : seat,
    ),
  };
};

/**
 * The name as it reads ON A POD: "The Promo Chaser" → "Promo Chaser". Prose keeps the article - "The Vacuum
 * bets $30 into $43" is how you say it - but a pod is 64px wide and "The " is four characters of nothing.
 *
 * TB3 made this load-bearing rather than cosmetic. The 9-handed ring fills the two right-most pods for the
 * first time, and it fills them with the longest names on the roster (the Promo Chaser and the Table
 * Captain, the local full-ring types). `.nm` is `white-space: nowrap`, so a name wider than the pod paints
 * straight out of it and off the frame - "The Promo Chase". Dropping the article buys back ~23px and lands
 * every name inside the felt. (`.seat .nm` also ellipsizes now, as the hard net for any future long name.)
 *
 * It reads better besides: the pod now speaks the same vocabulary as the read chips you tag it with -
 * "Whale", "Rock" - instead of "The Whale" next to a chip that says "Whale".
 */
const podName = (display: string): string => display.replace(/^The /, '');
const ARCH_NAME_LOOKUP: Record<string, string> = Object.fromEntries(
  Object.entries(VILLAIN_DISPLAY).map(([id, name]) => [id, name as string]),
);

/** Build a snapshot for the hero decision at `state`, threading the previous street's facts for whatChanged. */
const buildSnapshot = (
  handId: string,
  decisionIndex: number,
  state: HandState,
  heroCards: readonly [CardIndex, CardIndex],
  villain: PlayerState,
  prev: EngineFacts | undefined,
): { snapshot: PostflopSnapshot; refFacts: EngineFacts } => {
  const { ctx, rec, equity } = buildAdvice(state, heroCards, villain);
  const hero = state.players.find((p) => p.seat === state.spec.heroSeat)!;
  const legal = legalActions(state);
  const pot = ctx.price.pot;
  const heroPos: Position = hero.position;

  // reference facts: assemble the facts on the ENGINE's own recommended line (so the coach text + the
  // whatChanged delta are the engine's read of the spot, available before the user has chosen anything).
  const refUser: UserDecision = rec.sizingIntent
    ? { action: rec.finalAction, sizingIntent: rec.sizingIntent }
    : { action: rec.finalAction };
  const refGrade = gradeDecision({
    ctx,
    recommendation: rec,
    user: refUser,
    evInputs: { equity: ctx.hero.equity, equityVsContinue: ctx.hero.equity, foldProb: foldProbFor(villain), tournament: false },
  });
  const factsInput = {
    handId,
    decisionIndex,
    street: state.street,
    // ST-LIB: the game-type + ICM condition come from the SPEC, not a hardcode. A cash spec carries
    // gameType 'cash' and no tournament block, so icmPressure stays undefined and this is byte-identical
    // to the old hardcode. A tournament spec carries gameType 'tournament' and its engine-derived
    // icmPressure, which flows into the card-free digest → the coachCacheKey (so tournament spots bucket
    // distinctly) AND the coach prompt (`ICM ${pressure}`, so the baked prose speaks the condition).
    context: { gameType: state.spec.gameType, mode: 'casino_lab' as const, position: heroPos, positionClass: ctx.positionClass, playersInHand: ctx.playersInHand, icmPressure: state.spec.tournament?.icmPressure },
    math: { pot, toCall: ctx.price.toCall, effectiveStack: effectiveStackFor(state, hero.seat), spr: ctx.spr },
    hero: { holeCards: heroCards as [CardIndex, CardIndex], handClass: ctx.hero.handClass, drawFlags: ctx.hero.drawFlags, equity },
    board: { cards: state.board, textureClass: ctx.textureClass },
    villain: { seat: villain.seat, archetype: ctx.villain.archetype, rangeShape: ctx.villain.rangeShape },
    heroPerceived: heroPerceivedFrom(state, hero),
    recommendation: rec,
    grade: refGrade,
    prev,
  };
  const refFacts = assembleFacts(factsInput);
  const coach = renderAllTemplates(refFacts);
  const digest = projectDigest(refFacts); // card-free projection; the narrator's ONLY input (GH5).

  const snapshot: PostflopSnapshot = {
    kind: 'postflop',
    streetIndex: STREET_ORDER.indexOf(state.street),
    street: state.street,
    state,
    felt: feltFor(state),
    legal,
    ramp: legalToActionRamp(legal),
    price: { pot, toCall: ctx.price.toCall },
    recommendation: rec,
    bookAction: legalizeAction(rec.finalAction, legal),
    villainArchetypeId: villain.archetypeId!,
    villainSeat: villain.seat,
    heroEquityPct: Math.round(ctx.hero.equity * 100),
    coach,
    digest,
    grade: (user: UserDecision): LiveDecisionResult => {
      const typedAmount = amountForIntent(user, pot, state.currentBet, legal);
      const grade = gradeDecision({
        ctx,
        recommendation: rec,
        user: { ...user, typedAmount },
        evInputs: { equity: ctx.hero.equity, equityVsContinue: ctx.hero.equity, foldProb: foldProbFor(villain), tournament: false },
      });
      const facts = assembleFacts({ ...factsInput, grade });
      return { grade, coach: renderAllTemplates(facts), facts };
    },
  };
  return { snapshot, refFacts };
};

/** Hero's last-resort action: call a bet, else check - never fold, never raise. Only reached where hero has
 *  no read to act on (no live villain to face) or an autopilot halts; every REAL hero decision now comes
 *  from `heroActionFor` below. Kept because the walk must always be able to produce a legal action.
 *
 *  FULL: this used to BE hero's whole postflop line, which is why the table's carried stacks belonged to a
 *  calling station rather than to you, and why Observe announced "I act: raise" and then check-called. */
const stayAliveAction = (state: HandState, heroSeat: number): Action => {
  const legal = legalActions(state);
  const pick = legal.find((l) => l.kind === 'check') ?? legal.find((l) => l.kind === 'call') ?? legal[0];
  return { seat: heroSeat, kind: pick.kind };
};

/**
 * FULL - hero's PRO line at a postflop decision: the engine's own recommendation, legalized and driven to a
 * concrete amount. This is the same legalize+size `buildCounterfactual` has always used to replay "the line
 * you should have taken"; the walk now plays it instead of check-calling past it.
 *
 * Why this is the right default for an unplayed decision: Observe's whole promise is "watch the pro play
 * the hand", and `liveObserveStreet` already narrates this exact action as a beat tagged "I act". Playing
 * it makes the felt match the narration. In Diagnose/Solo it is only ever a PROVISIONAL stand-in for a
 * decision you have not reached yet - `applyHeroDecision` overwrites it with your real action the moment
 * you make one.
 */
const proLineAction = (snap: PostflopSnapshot, state: HandState, heroSeat: number): Action => {
  const kind = snap.bookAction;
  const legal = legalActions(state);
  if (!legal.some((l) => l.kind === kind)) return stayAliveAction(state, heroSeat); // never force an illegal line
  const user: UserDecision = snap.recommendation.sizingIntent
    ? { action: kind, sizingIntent: snap.recommendation.sizingIntent }
    : { action: kind };
  const amount = amountForIntent(user, snap.price.pot, state.currentBet, legal);
  return { seat: heroSeat, kind, ...(amount !== undefined ? { amount } : {}) };
};

/**
 * FULL - the hero decisions the USER has actually played this hand, by step.
 *
 * TB0 §3 rule 3 is "hero decides, villains scripted". Honoring it means the walk cannot invent hero's line:
 * anything recorded here is replayed as-is and IS what happened, and only the not-yet-reached tail falls
 * back to the pro line. Because the walk is deterministic in `spec.seed`, replaying from the initial state
 * with the same recorded actions reproduces the villains' RNG exactly, so the prefix of a rebuilt hand is
 * identical to the one you already played - the tail is the only thing that moves.
 */
export interface HeroLine {
  /**
   * hero's real preflop actions, indexed by PREFLOP decision number.
   *
   * FULL (re-opened): this was a single `Action`, on the assumption that hero acts preflop once. He does not
   * - he 3-bets, a villain 4-bets, and he is asked again (TP1 measured 3-bet pots at 9.5% of hands, 4-bet+ at
   * 1.9%). One action could only be replayed at EVERY hero preflop turn, which either threw (his $36 3-bet is
   * not a legal raise at a node whose band is [252, 300] - the blank screen) or, worse, silently played a
   * second action he never chose and carried those chips into the session. A list indexed by decision number
   * is the same shape `postflop` below has always had, for the same reason.
   */
  preflop?: readonly (Action | undefined)[];
  /** hero's real postflop actions, indexed by postflop decision number (flop → turn → river). */
  postflop?: readonly (Action | undefined)[];
}

export interface DealOptions {
  seed: string;
  /** leak-biased spot targeting (S15 spotWeights) - biases WHICH family of spot gets dealt. */
  spotWeights?: ScenarioSpec['spotWeights'];
  /** minimum hero decisions required (default 1). The caller retries seeds to hit this. */
  minDecisions?: number;
  /** PF3: opt into the played-preflop DO-mode path - hero plays his real preflop line and the hand opens
   *  on a preflop decision (`model.preflop` + `steps[0]`). Default off (the Observe/GH path is unchanged). */
  preflopPlay?: boolean;
}

/** The options `dealSessionHand` takes - the continuous-table deal (TB2) plus FULL's played hero line. */
export interface DealSessionOptions {
  /** PF3/PF4: hero plays his real preflop line and the hand opens on a preflop decision. */
  preflopPlay?: boolean;
  /** FULL: the decisions hero has REALLY played this hand. Omitted on a fresh deal (nothing played yet);
   *  supplied by `applyHeroDecision` when it rebuilds the hand around your action. */
  heroLine?: HeroLine;
}

/** The 4 primary-villain archetypes the read UI can tag (nickname → id): Vacuum/Firecracker/Rock/Whale.
 *  The main opponent is picked from these per deal (seeded), so the read axis has real, taggable variety
 *  while the 5-tag UI stays correct. Wider villain variety across ALL seats is GH4's job. */
const TAGGABLE_PRIMARY: ArchetypeId[] = ['sticker', 'firestarter', 'rock', 'tourist'];

/** The base $1/$3 live spec the trainer deals from: a loose-passive table, hero last, the main opponent
 *  seeded from the taggable set, a Tourist behind. */
export const baseSpec = (seed: string, spotWeights?: ScenarioSpec['spotWeights']): ScenarioSpec => {
  const primary = TAGGABLE_PRIMARY[Math.floor(createRng(`${seed}::primary`).next() * TAGGABLE_PRIMARY.length)];
  return {
    gameType: 'cash',
    tableSize: 6,
    stakes: { sb: 1, bb: 3, currency: 'USD' },
    heroSeat: 0,
    heroPosition: 'BTN',
    stackDepthBB: 150,
    villains: [
      { seat: 1, archetypeId: primary },
      { seat: 2, archetypeId: 'tourist' },
    ],
    tableType: 'loosePassive',
    seed,
    ...(spotWeights ? { spotWeights } : {}),
  };
};

const DEPS = { archetype: archetypeOf };

/**
 * Hero's REAL starting stack for this hand: what is still in front of him plus what he has already put
 * in. Exact at any `acting` state (a decision point), because no pot has been awarded yet.
 *
 * TB0 finding #8: the old `spec.stackDepthBB * spec.stakes.bb` was a table-wide OPENING default. Once a
 * continuous table carries chips, seats diverge and that value is a lie for every hand after the first.
 * Reading the state is honest at any table size, at any hand number, for any per-seat amount.
 */
const heroStartStackAt = (state: HandState, heroSeat: number): number => {
  const p = state.players.find((pp) => pp.seat === heroSeat);
  if (!p) throw new Error(`heroStartStackAt: no player at hero seat ${heroSeat}`);
  return p.stack + p.committedTotal;
};

/** PF3: the played-preflop deps (loaders + table facts) for a spec, from the shipped browser corpus. */
const preflopDepsFor = (spec: ScenarioSpec): PreflopPlayDeps => ({
  archetype: archetypeOf,
  rangeSetOf,
  rules: RULES,
  tableType: tableTypeFamily(spec.tableType),
  tableSize: spec.tableSize <= 6 ? 6 : 9,
  primaryVillainSeat: spec.villains[0].seat,
});

/**
 * TB2 - the villain hero is ACTUALLY up against, resolved from the live state.
 *
 * `spec.villains[0].seat` was a safe shorthand at the 3-handed baseSpec (seat 1 was the only real
 * opponent). At a 7-handed continuous table it is an arbitrary stranger who has usually folded before the
 * flop, so using it would point the read, the rules, and the grade at the wrong player. The honest answer
 * is the seat hero is reacting to: the last live aggressor on the current street, else the last live
 * preflop raiser (the opener still drives the pot on a checked flop), else any live villain.
 */
const villainHeroFaces = (state: HandState): number | undefined => {
  const heroSeat = state.spec.heroSeat;
  const live = (seat: number): boolean => {
    const p = state.players.find((pp) => pp.seat === seat);
    return !!p && !p.folded && p.seat !== heroSeat;
  };
  const aggressive = (kind: ActionKind): boolean => kind === 'bet' || kind === 'raise' || kind === 'jam';
  // 1. whoever led THIS street - the bet hero is answering right now.
  for (let i = state.history.length - 1; i >= 0; i--) {
    const h = state.history[i];
    if (h.street !== state.street) break;
    if (h.action.seat !== heroSeat && aggressive(h.action.kind) && live(h.action.seat)) return h.action.seat;
  }
  // 2. nobody has led this street - fall back to the preflop aggressor, who still owns the initiative.
  for (let i = state.history.length - 1; i >= 0; i--) {
    const h = state.history[i];
    if (h.street !== 'preflop') continue;
    if (h.action.seat !== heroSeat && aggressive(h.action.kind) && live(h.action.seat)) return h.action.seat;
  }
  // 3. a limped, checked-around pot - any live villain is the honest read subject.
  return state.players.find((p) => p.seat !== heroSeat && !p.folded && p.archetypeId)?.seat;
};

/** TB2: the played-preflop deps for a SESSION hand - the baseSpec deps plus the live primary-villain
 *  resolver, so a 7-handed preflop matches rules against the player hero is actually facing. */
const sessionPreflopDepsFor = (spec: ScenarioSpec): PreflopPlayDeps => ({
  ...preflopDepsFor(spec),
  resolvePrimaryVillain: villainHeroFaces,
});

/**
 * TB4 - TP1's villain preflop policy, wired on the SESSION PATH ONLY (Joe's call, 2026-07-15).
 *
 * TP1 shipped `decideVillainPreflop` as opt-in and nothing passed it, so the running app's villains still
 * played the position-blind open/call autopilot: `scriptVillainAction` fell through to the POSTFLOP action
 * model on an empty board, which cannot see the seat at all (2 of 8 archetypes actually opened the BUTTON
 * *tighter* than under the gun). TB4 exists to make watching those villains teach, so narrating that line
 * would be narrating a bug.
 *
 * Scoped to the session deliberately, NOT wired into `DEPS`/`generate()`. That one shared `DEPS` object
 * also feeds GH4's curated-library `baseSpec` path, whose 24 pre-baked GH7 prose entries are keyed by a
 * hash of the hands it produces - switching it on globally moves those keys and `narrationLibrary.test.ts`
 * fails by design. Same precedent as TB2's `resolvePrimaryVillain`: supplied here, omitted there.
 */
const sessionVillainPolicy = (spec: ScenarioSpec): VillainPreflopPolicy => {
  const deps = { rangeSetOf, tableSize: (spec.tableSize <= 6 ? 6 : 9) as 6 | 9 };
  return (state, seat, arch, cards, rng) => decideVillainPreflop(state, seat, arch, cards, rng, deps);
};

/** A coarse villain range shape for the BOARDLESS preflop facts - there is no board to bucket against, so
 *  a neutral shape is honest (the preflop coach reasons over the archetype + line, not a postflop shape). */
const PREFLOP_VILLAIN_SHAPE = { valueHeavyPct: 50, bluffPct: 15, topClasses: [] as string[] };

/** Assemble the PF3 step-0 preflop snapshot from hero's resolved first preflop decision (boardless facts
 *  + card-free digest + the preflop-typed recommendation + the PF2 grade fn). */
const buildPreflopSnapshot = (
  handId: string,
  decision: HeroPreflopDecision,
  heroCards: readonly [CardIndex, CardIndex],
  villainSeat: number,
  /** which preflop decision this is (0 = hero's first). Hero can face several - see `HeroLine.preflop`. */
  decisionIndex = 0,
): PreflopSnapshot => {
  const state = decision.state;
  const hero = state.players.find((p) => p.seat === state.spec.heroSeat)!;
  const arch = archetypeOf(state.players.find((p) => p.seat === villainSeat)!.archetypeId!);
  const facts: PreflopEngineFacts = assemblePreflopFacts({
    handId,
    decisionIndex,
    context: {
      gameType: 'cash',
      mode: 'casino_lab',
      position: decision.heroPosition,
      positionClass: decision.positionClass,
      playersInHand: state.players.filter((p) => !p.folded).length,
    },
    hero: { holeCards: heroCards as [CardIndex, CardIndex] },
    math: {
      role: decision.role,
      potType: decision.potType,
      effectiveStackBB: decision.effectiveStackBB,
      toCallBB: decision.toCallBB,
      potBB: decision.potBB,
      openerPosition: decision.openerPosition,
    },
    villain: { seat: villainSeat, archetype: arch, rangeShape: PREFLOP_VILLAIN_SHAPE },
    recommendation: decision.recommendation,
  });
  const digest: PreflopFactsDigest = projectPreflopDigest(facts);
  const legal = legalActions(state);
  return {
    kind: 'preflop',
    streetIndex: 0,
    street: 'preflop',
    state,
    felt: feltFor(state),
    legal,
    ramp: legalToActionRamp(legal),
    price: { pot: potTotal(state), toCall: Math.max(0, state.currentBet - hero.committedThisStreet) },
    recommendation: decision.recommendation,
    villainArchetypeId: arch.id,
    villainSeat,
    facts,
    digest,
    grade: decision.grade,
  };
};

/**
 * Deal ONE fresh live hand and walk it to showdown, capturing every hero postflop decision. Deterministic
 * in `seed` (same seed → identical hand), so the deal picker gets reproducibility + anti-repeat for free.
 *
 * PF3: `opts.preflopPlay` opts into the played-preflop DO-mode path - hero's REAL recommendPreflop line is
 * played (villain ranges then narrow off it) and hero's first preflop decision is exposed as step 0 of the
 * stepper (`model.preflop` + `model.steps`). Default (off): the scaffolding open/call autopilot, so the
 * shipped Observe/GH path is byte-for-byte unchanged.
 */
export const dealLiveHand = (spec: ScenarioSpec, opts: { preflopPlay?: boolean } = {}): LiveHandModel => {
  const preflopDeps = preflopDepsFor(spec);
  const heroAutopilot = opts.preflopPlay
    ? makeHeroPlayedPreflopAutopilot(preflopDeps)
    : preflopOpenCallAutopilot;
  const scenario = generate(spec, DEPS, { heroAutopilot });
  // CRITICAL: generate() rejection-samples and returns the hand under the WINNING seed (`spec.seed#aK`,
  // always at least `#a0`), and the whole walk (advance → board/showdown, via state.spec) uses THAT seed.
  // So the deal authority for hero cards + villain scripting must be the scenario's spec, NOT the input
  // spec - otherwise the felt shows the scenario's hero hand while the advice/grade/coach are computed on
  // a different (original-seed) deal. Using `scenario.spec` keeps the shown hand and the coached hand the
  // same one (fixes the felt↔advice hero-card divergence).
  const dealtSpec = scenario.spec;
  const handId = `gh3:${spec.seed}`;
  const heroSeat = spec.heroSeat;
  const n = spec.villains.length + 1;
  const deal = dealForSpec(dealtSpec, n);
  const heroCards = deal.holeCards[heroSeat] as [CardIndex, CardIndex];
  const villainRng = createRng(`${dealtSpec.seed}::villain`);
  // the main opponent: the first still-live villain seat with an archetype (drives the read + advice).
  const primaryVillainSeat = spec.villains[0].seat;

  let state = scenario.decisionPoint.state;
  // captured BEFORE the walk: `state` below is driven to a terminal where the pots are already awarded.
  const startStack = heroStartStackAt(state, heroSeat);
  const snapshots: PostflopSnapshot[] = [];
  let prevFacts: EngineFacts | undefined;
  const guard = n * 60;

  for (let i = 0; i < guard; i++) {
    if (state.phase !== 'acting' || state.toAct === null) break;
    const seat = state.toAct;
    if (seat === heroSeat) {
      if (state.street === 'preflop') {
        // shouldn't happen (generate auto-plays preflop), but stay-alive if it does.
        state = advance(state, stayAliveAction(state, heroSeat));
        continue;
      }
      const villain =
        state.players.find((p) => p.seat === primaryVillainSeat && !p.folded) ??
        state.players.find((p) => p.seat !== heroSeat && !p.folded);
      if (!villain) {
        state = advance(state, stayAliveAction(state, heroSeat));
        continue;
      }
      const { snapshot, refFacts } = buildSnapshot(handId, snapshots.length, state, heroCards, villain, prevFacts);
      snapshots.push(snapshot);
      prevFacts = refFacts;
      state = advance(state, stayAliveAction(state, heroSeat));
    } else {
      const villain = state.players.find((p) => p.seat === seat)!;
      state = advance(state, scriptVillainAction(state, seat, archetypeOf(villain.archetypeId!), deal, villainRng));
    }
  }

  const hero = state.players.find((p) => p.seat === heroSeat)!;
  // the OPPONENT the read/advice is about is the villain hero actually faces at his first decision - NOT
  // the nominal primary seat, which may have folded preflop (then the runner used a live fallback). Using
  // the first snapshot's villain keeps the headline + read tag + grade all about the same live player.
  const opponentArch = (snapshots[0]?.villainArchetypeId ?? spec.villains[0].archetypeId) as ArchetypeId;

  // PF3: the played-preflop step 0. Resolved by replaying the WINNING (scenario) seed to hero's first
  // preflop decision - so it matches the exact hand the postflop walk flowed from (same felt↔advice
  // discipline as the GH7 fix). Only on the opt-in DO-mode path; null when hero never acted preflop.
  let preflop: PreflopSnapshot | undefined;
  if (opts.preflopPlay) {
    const decision = resolveHeroFirstPreflop(dealtSpec, preflopDeps, archetypeOf);
    if (decision) preflop = buildPreflopSnapshot(handId, decision, heroCards, primaryVillainSeat);
  }
  // FULL (re-opened): this path resolves hero's FIRST preflop decision only, and deliberately stays that
  // way. It is not a continuous-table hand - nobody plays a decision on it (`done: true`,
  // `applyHeroDecision` is never called), so it has no node to ask hero about a second time. The list keeps
  // the model shape uniform for the surfaces that read it.
  const preflops: PreflopSnapshot[] = preflop ? [preflop] : [];
  const steps: LiveSnapshot[] = [...preflops, ...snapshots];

  return {
    handId,
    seed: spec.seed,
    spec: dealtSpec, // the winning-seed spec - the deal authority GH6 re-advances from (must match the felt)
    heroSeat,
    heroCards,
    villainSeat: primaryVillainSeat,
    villainArchetypeId: opponentArch,
    villainName: displayName(opponentArch),
    snapshots,
    preflops,
    preflop,
    steps,
    terminal: state,
    heroNetChips: hero.stack - startStack,
    // FULL: the GH4 curated-library path is not a continuous-table hand - it is a fixed, pre-authored
    // scenario nobody harvests, and `applyHeroDecision` is never called on it. It reports `done` so nothing
    // mistakes it for a hand still waiting on hero, and carries `scenario.decisionPoint.state` as its
    // initial so the field is never a lie about where the walk began.
    initial: scenario.decisionPoint.state,
    heroLine: {},
    opts: { preflopPlay: opts.preflopPlay },
    done: true,
  };
};

/** Deal a hand that has at least `minDecisions` hero decisions, scanning seeds off `opts.seed` so the UI
 *  always gets a playable multi-street hand. Deterministic: the SAME opts.seed yields the SAME hand. */
export const dealPlayableHand = (opts: DealOptions): LiveHandModel => {
  const min = opts.minDecisions ?? 1;
  let last: LiveHandModel | null = null;
  for (let i = 0; i < 40; i++) {
    const seed = i === 0 ? opts.seed : `${opts.seed}#${i}`;
    let model: LiveHandModel;
    try {
      model = dealLiveHand(baseSpec(seed, opts.spotWeights), { preflopPlay: opts.preflopPlay });
    } catch {
      // A rare hand hits a latent evaluator edge in the GH1 advice pipeline (@poker-apprentice
      // describeHand throws "C is not iterable" on certain range×board evaluations). It is unusable, so
      // skip it and deal another - same posture as skipping a preflop-fold. Deterministic: the seed order
      // is fixed, so the skips are reproducible. (Latent bug flagged for a separate fix; PF3 does not own it.)
      continue;
    }
    last = model;
    // on the played-preflop path also require the preflop step (hero actually acted preflop, didn't fold).
    const preflopOk = !opts.preflopPlay || model.preflop !== undefined;
    if (model.snapshots.length >= min && preflopOk) return model;
  }
  if (last) return last; // fallback: a real hand, just fewer decisions (or no preflop step) than hoped.
  throw new Error(`dealPlayableHand: no usable hand for seed '${opts.seed}' in 40 attempts`);
};

// ── TB2: the CONTINUOUS-TABLE deal ────────────────────────────────────────────────────────────────────
// dealLiveHand routes through generate(), which builds its OWN hand: it picks the button itself
// (resolveButtonSeat) and calls createHand with no startingStacks. Both are fatal for a continuous table -
// the session owns the button (it rotates) and owns the stacks (they carry). So a session hand walks from
// a HandState the session ALREADY built (TB1's dealNextHand → createHand with buttonSeat + startingStacks)
// rather than generating a new one.
//
// What is deliberately given up: generate()'s rejection sampling / spotWeights targeting. At a real table
// you play the hand you are dealt - you cannot re-deal until a spot family shows up. The S15
// leak→spotWeights hook does not apply to a session hand and is not silently half-wired; see the note on
// dealPlayableSessionHand for the one bounded re-deal that IS kept, and why.

/**
 * Walk a session hand from the state the session built, capturing every hero decision.
 *
 * Same walk + same advice chain as `dealLiveHand`, with three differences forced by the continuous table:
 * `initial` is the authority (never re-generated), preflop is auto-played HERE rather than inside
 * generate(), and the primary villain is resolved live (`villainHeroFaces`) rather than pinned to
 * `villains[0]`.
 *
 * Deterministic in `initial.spec.seed`.
 */
export const dealSessionHand = (initial: HandState, opts: DealSessionOptions = {}): LiveHandModel => {
  const heroLine = opts.heroLine;
  const spec = initial.spec;
  const heroSeat = spec.heroSeat;
  const n = spec.villains.length + 1;
  const preflopDeps = sessionPreflopDepsFor(spec);
  const handId = initial.handId;
  // createHand deals from spec.seed, so dealForSpec reproduces the exact cards already in `initial` -
  // this is a read of the same deal, not a second one.
  const deal = dealForSpec(spec, n);
  const heroCards = deal.holeCards[heroSeat] as [CardIndex, CardIndex];
  const villainRng = createRng(`${spec.seed}::villain`);
  // TB4: the session's villains play TP1's real chart+knob preflop (see sessionVillainPolicy).
  const preflopPolicy = sessionVillainPolicy(spec);

  let state = initial;
  const startStack = heroStartStackAt(state, heroSeat);
  const snapshots: PostflopSnapshot[] = [];
  // FULL (re-opened): every preflop node hero faces, built ON THE WAY PAST rather than re-resolved after the
  // walk. The old code resolved a single step-0 snapshot afterwards from a remembered `heroFirstPreflop`
  // state, which structurally could not see a second node.
  const preflops: PreflopSnapshot[] = [];
  let prevFacts: EngineFacts | undefined;
  // hero's FIRST preflop decision, captured on the way past so the read subject can be resolved below
  // without replaying the hand (resolveHeroFirstPreflop would re-derive the button and lose the stacks).
  let heroFirstPreflop: HandState | undefined;
  const guard = n * 60;

  for (let i = 0; i < guard; i++) {
    if (state.phase !== 'acting' || state.toAct === null) break;
    const seat = state.toAct;
    if (seat === heroSeat) {
      if (state.street === 'preflop') {
        if (!heroFirstPreflop) heroFirstPreflop = state;
        // which preflop decision this is. `preflops` only fills on the played-preflop path, so the Observe/GH
        // path indexes off the same counter without building snapshots it has no surface for.
        const pk = preflops.length;
        let provisional: Action;
        if (opts.preflopPlay) {
          // resolve the node ONCE and use it for both the snapshot and the pro's provisional line -
          // mechanicalPreflopAction over this recommendation is precisely what the played-preflop autopilot
          // returns, so this is the same line, without paying for recommendPreflop twice.
          const decision = resolveHeroPreflopAt(state, deal, preflopDeps);
          const pfVillain = villainHeroFaces(state) ?? spec.villains[0].seat;
          preflops.push(buildPreflopSnapshot(handId, decision, heroCards, pfVillain, pk));
          provisional = mechanicalPreflopAction(decision.recommendation, state, heroSeat);
        } else {
          const act = preflopOpenCallAutopilot(state, deal, heroSeat);
          // the autopilot always returns an Action on preflop; stay alive if it ever halts here.
          provisional = 'stop' in act ? stayAliveAction(state, heroSeat) : act;
        }
        // FULL: your REAL action AT THIS NODE if you have played it; else the chart line, which is what
        // Observe means by "the pro plays it" and what Diagnose/Solo show you before you act. Indexed by
        // `pk`, so a decision you have not reached is never filled in with one you made somewhere else.
        state = advance(state, heroLine?.preflop?.[pk] ?? provisional);
        continue;
      }
      const villainSeat = villainHeroFaces(state);
      const villain = villainSeat !== undefined ? state.players.find((p) => p.seat === villainSeat) : undefined;
      if (!villain) {
        state = advance(state, stayAliveAction(state, heroSeat));
        continue;
      }
      const k = snapshots.length;
      const { snapshot, refFacts } = buildSnapshot(handId, k, state, heroCards, villain, prevFacts);
      snapshots.push(snapshot);
      prevFacts = refFacts;
      // FULL: your REAL action at this decision if you have played it; else the pro's line as a provisional
      // tail. This is the line that makes the harvested stacks yours (TB0 §3 rule 3).
      state = advance(state, heroLine?.postflop?.[k] ?? proLineAction(snapshot, state, heroSeat));
    } else {
      const villain = state.players.find((p) => p.seat === seat)!;
      state = advance(
        state,
        scriptVillainAction(state, seat, archetypeOf(villain.archetypeId!), deal, villainRng, preflopPolicy),
      );
    }
  }

  const hero = state.players.find((p) => p.seat === heroSeat)!;
  // the read subject: the villain hero actually faced at his first decision (postflop if there was one,
  // else the preflop opponent) - never the nominal seat-1 stranger.
  const readSeat =
    snapshots[0]?.villainSeat ??
    (heroFirstPreflop ? villainHeroFaces(heroFirstPreflop) : undefined) ??
    spec.villains[0].seat;
  const readArch = (state.players.find((p) => p.seat === readSeat)?.archetypeId ??
    spec.villains[0].archetypeId) as ArchetypeId;

  const preflop = preflops[0];
  const steps: LiveSnapshot[] = [...preflops, ...snapshots];

  // FULL - is every hero decision in this hand a REAL one? The walk fell back to the pro line at any step
  // hero has not played, so the hand is only finished (and the terminal only harvestable) when the recorded
  // line covers every step the walk actually produced.
  //
  // Re-opened: this asked whether hero had played "the" preflop, singular. On a hand where he 3-bets and gets
  // 4-bet that read TRUE with a node still unplayed, so a hand the pro had quietly finished for him counted
  // as his and was harvestable. Every preflop node now has to be his, exactly as every postflop one does.
  const preflopDone = preflops.every((_, k) => heroLine?.preflop?.[k] !== undefined);
  const postflopDone = snapshots.every((_, k) => heroLine?.postflop?.[k] !== undefined);
  const done = preflopDone && postflopDone;

  return {
    handId,
    seed: spec.seed,
    spec,
    heroSeat,
    heroCards,
    villainSeat: readSeat,
    villainArchetypeId: readArch,
    villainName: displayName(readArch),
    snapshots,
    preflops,
    preflop,
    steps,
    terminal: state,
    heroNetChips: hero.stack - startStack,
    initial,
    heroLine: heroLine ?? {},
    opts,
    done,
  };
};

/**
 * FULL - hero really played `decision` at `stepIndex`. Rebuild the hand around it.
 *
 * This is the function that makes the carried stacks yours. The walk is deterministic in `spec.seed`, so
 * replaying from `model.initial` with your action recorded reproduces the villains' RNG exactly: every step
 * BEFORE yours is the hand you already saw, your step is now really played, and the tail beyond it is
 * re-derived from the state your action actually produced. Fold, raise, or jam and the board, the villains'
 * responses, and the terminal all move with you - which they could not do when the runner pre-walked the
 * whole hand before you acted.
 *
 * Pure: returns a NEW model, never mutates the one passed in.
 */
export const applyHeroDecision = (
  model: LiveHandModel,
  stepIndex: number,
  action: ActionKind,
  amount?: string,
): LiveHandModel => {
  const step = model.steps[stepIndex];
  if (!step) return model;
  const played = heroActionFor(step.state, model.heroSeat, action, amount);

  // record the action at the node it was played AT - `indexOf` on the step arrays, exactly as the postflop
  // side has always done. (The two arrays hold the same object references `steps` does; `withReads` and
  // `applyMode` re-map all of them together, so identity holds after a re-paint.)
  const heroLine: HeroLine =
    step.kind === 'preflop'
      ? { ...model.heroLine, preflop: recordAt(model.heroLine.preflop, model.preflops.indexOf(step), played) }
      : { ...model.heroLine, postflop: recordAt(model.heroLine.postflop, model.snapshots.indexOf(step), played) };

  return dealSessionHand(model.initial, { ...model.opts, heroLine });
};

/**
 * Hero's concrete, legal Action from what he actually chose on the ramp.
 *
 * Uses the DOLLARS THE USER TYPED, not a re-derived sizing bucket. That matters: the trainer's whole
 * do-your-own-math premise is that the number is yours, and the grader only buckets it to SCORE the sizing
 * fit (`userDecisionFrom`). Bucketing it to PLAY it would silently round your $17 bet to "two-thirds pot"
 * and put a different number in the pot than the one you typed. Clamped to the engine's legal min/max, so a
 * fat-fingered amount is corrected rather than throwing.
 *
 * Works for preflop and postflop alike - both ramps hand back an action plus a typed amount, and the engine
 * legalizes in dollars either way.
 */
const heroActionFor = (state: HandState, heroSeat: number, kind: ActionKind, amount?: string): Action => {
  const legal = legalActions(state);
  const match = legal.find((l) => l.kind === kind);
  if (!match) return stayAliveAction(state, heroSeat); // an illegal pick can't be played; never throw at the felt
  if (amount === undefined) return { seat: heroSeat, kind };
  const typed = Number(String(amount).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(typed)) return { seat: heroSeat, kind };
  const clamped = Math.min(match.max ?? typed, Math.max(match.min ?? typed, typed));
  return { seat: heroSeat, kind, amount: clamped };
};

/** write `action` at `k` in a sparse hero line, without mutating the original. */
const recordAt = (
  line: readonly (Action | undefined)[] | undefined,
  k: number,
  action: Action,
): readonly (Action | undefined)[] => {
  const next = [...(line ?? [])];
  while (next.length <= k) next.push(undefined);
  next[k] = action;
  return next;
};

// ── GH6: the counterfactual "right line" replay ───────────────────────────────────────────────────────
// After a WRONG decision (chiefly a wrong fold) the UI offers an OPTIONAL, clearly-labeled replay of the
// CORRECT line played out to showdown. This re-runs the SAME deterministic engine from the decision point,
// substituting the engine's own recommended action (snap.bookAction - the legalized recommendation) for
// what hero actually did, then walking the hand out (villains scripted, hero stays alive on later streets).
// The result is serialized to a HandRecord, the exact shape segmentHand (S26) already renders in the
// ReplayTimeline. Pure + deterministic in the model + index - no new strategy, no API call.

export interface Counterfactual {
  /** the correct line replayed to showdown, as ReplaySegments the S26 <ReplayTimeline> renders directly.
   *  Built from the authoritative terminal HandState (not a flat HandRecord) so the button + streets are
   *  exact - a generated hand's history omits pre-decision preflop folds, which breaks segmentHand's
   *  reconstruction (see segmentsFromHandState). */
  segments: ReplaySegments;
  /** the corrective action the engine recommended - the line hero SHOULD have taken (call/bet/raise/jam). */
  correctAction: ActionKind;
  /** did the correct line net hero chips? (NET, matching the import derived-result convention). */
  heroWon: boolean;
  heroNetChips: number;
}

/** Build the GH6 counterfactual for the hero decision at `snapshotIndex`: replay the engine's recommended
 *  line from that point to showdown. Returns null when there is nothing to show - the index is invalid, or
 *  the engine itself recommends folding (then the fold was correct and there is no alternative line). */
export const buildCounterfactual = (model: LiveHandModel, snapshotIndex: number): Counterfactual | null => {
  const snap = model.snapshots[snapshotIndex];
  if (!snap) return null;
  const correct = snap.bookAction;
  if (correct === 'fold') return null; // the engine agrees with folding - no alternative to replay

  const { spec } = model;
  const heroSeat = model.heroSeat;
  const n = spec.villains.length + 1;
  const deal = dealForSpec(spec, n);
  const villainRng = createRng(`${spec.seed}::cf-villain`);
  const startStack = heroStartStackAt(snap.state, heroSeat);

  // (1) hero's corrective action at the fold decision point: the engine's recommended line, legalized,
  //     with the recommended sizing driven to a concrete legal amount for bet/raise.
  let state = snap.state;
  const legal0 = legalActions(state);
  const user: UserDecision = snap.recommendation.sizingIntent
    ? { action: correct, sizingIntent: snap.recommendation.sizingIntent }
    : { action: correct };
  const amount = amountForIntent(user, snap.price.pot, state.currentBet, legal0);
  state = advance(state, { seat: heroSeat, kind: correct, ...(amount !== undefined ? { amount } : {}) });

  // (2) walk the hand out to showdown: villains scripted (fresh CF rng), hero stays alive on later streets.
  const guard = n * 60;
  for (let k = 0; k < guard; k++) {
    if (state.phase !== 'acting' || state.toAct === null) break;
    const seat = state.toAct;
    if (seat === heroSeat) {
      state = advance(state, stayAliveAction(state, heroSeat));
    } else {
      const villain = state.players.find((p) => p.seat === seat)!;
      state = advance(state, scriptVillainAction(state, seat, archetypeOf(villain.archetypeId!), deal, villainRng));
    }
  }

  const hero = state.players.find((p) => p.seat === heroSeat)!;
  const heroNetChips = Math.round(hero.stack - startStack);
  const segments = segmentsFromHandState(state, model.heroCards, startStack);
  return { segments, correctAction: correct, heroWon: heroNetChips > 0, heroNetChips };
};

/** PF4: the counterfactual for a WRONG PREFLOP fold - replay hero's recommended preflop line (open / call /
 *  3-bet, legalized with the recommended sizing driven to a concrete amount) from the node's own state out
 *  to showdown, so the DO modes can show "what would've happened if you'd played it". Sibling to
 *  buildCounterfactual (which indexes the postflop snapshots); this one indexes `model.preflops`. Returns
 *  null when that node does not exist, or the engine itself recommends folding (then the fold was correct).
 *  Pure + deterministic in the model - reuses mechanicalPreflopAction (the same legalize+size the
 *  played-preflop autopilot uses), scriptVillainAction, and segmentsFromHandState. No new strategy, no API
 *  call.
 *
 *  FULL (re-opened): `preflopIndex` is why this takes an argument at all. It read `model.preflop` - hero's
 *  FIRST node - so once he can fold a 4-bet, tapping "see how it would've played out" on THAT fold would
 *  have replayed his opening decision instead of the one he just got wrong. */
export const buildPreflopCounterfactual = (model: LiveHandModel, preflopIndex = 0): Counterfactual | null => {
  const snap = model.preflops[preflopIndex];
  if (!snap) return null;
  if (snap.recommendation.action === 'fold' || snap.recommendation.finalIntent === 'fold') return null;

  const { spec } = model;
  const heroSeat = model.heroSeat;
  const n = spec.villains.length + 1;
  const deal = dealForSpec(spec, n);
  const villainRng = createRng(`${spec.seed}::cf-preflop-villain`);
  const startStack = heroStartStackAt(snap.state, heroSeat);

  // (1) hero's corrective preflop action at the fold decision point (the recommended line, legalized, with
  //     the recommended bb sizing driven to a concrete legal amount for a raise).
  let state = snap.state;
  const heroAction = mechanicalPreflopAction(snap.recommendation, state, heroSeat);
  state = advance(state, heroAction);

  // (2) walk the hand out to showdown: villains scripted (fresh CF rng), hero stays alive on later streets.
  const guard = n * 60;
  for (let k = 0; k < guard; k++) {
    if (state.phase !== 'acting' || state.toAct === null) break;
    const seat = state.toAct;
    if (seat === heroSeat) {
      state = advance(state, stayAliveAction(state, heroSeat));
    } else {
      const villain = state.players.find((p) => p.seat === seat)!;
      state = advance(state, scriptVillainAction(state, seat, archetypeOf(villain.archetypeId!), deal, villainRng));
    }
  }

  const hero = state.players.find((p) => p.seat === heroSeat)!;
  const heroNetChips = Math.round(hero.stack - startStack);
  const segments = segmentsFromHandState(state, model.heroCards, startStack);
  return { segments, correctAction: heroAction.kind, heroWon: heroNetChips > 0, heroNetChips };
};

// ── TB4: the folded-hand observation walk ─────────────────────────────────────────────────────────────
// You folded. The table plays on (TB0 §9 F4) and you watch it for free - the cheapest read in poker.
// This is the ONE place the app builds "what happened while you were out", and it is where the hole-card
// firewall lives.
//
// THE FIREWALL IS BY CONSTRUCTION, NOT BY DISCIPLINE (the PF3 boardless-digest pattern):
//   1. `WatchedAction` HAS NO CARD FIELD. A folded villain's cards are unreachable from a FoldedWalk even
//      through a projection bug, because there is nowhere to put them.
//   2. This walk NEVER reads `dealForSpec().holeCards` for a villain. It reads the walked-out state's
//      `players[].holeCards`, which the engine populates ONLY at a real showdown: `hand/showdown.ts:59-62`
//      reveals exactly the seats that did not fold, and `awardUncontested` (`showdown.ts:14-20`) reveals
//      NOTHING. So a pot that ends without a showdown yields zero villain cards here - not because this
//      function hides them, but because they were never written into the state it reads. That is the same
//      rule the live table runs on: you see actions, sizings, and whatever is turned over at the end.
// `deal` is still needed - `scriptVillainAction` must know a villain's own cards to decide HIS action -
// but it is passed straight to the engine and never copied into the returned data.

/** One action hero WATCHED after folding. No card field, by construction (see the firewall note). */
export interface WatchedAction {
  seat: number;
  street: Street;
  kind: ActionKind;
  /** bet/raise/jam: the raise-TO amount. call: what the call cost him. check/fold: 0. */
  amount: number;
  /** the pot once this action resolved. */
  potAfter: number;
  /** the live aggressor this action answers - villain-vs-villain: who bet into whom. */
  vsSeat?: number;
  /** the first aggression on its street (a bet or a preflop open). */
  opensStreet: boolean;
  /** re-raises live aggression on this street. */
  isReraise: boolean;
}

/** A hand that was TURNED OVER. Only a real showdown mints one of these. */
export interface ShownHand {
  seat: number;
  cards: readonly [CardIndex, CardIndex];
  won: boolean;
}

/** Where a seat finished - everything the felt needs to draw the aftermath, and NO cards (the firewall:
 *  the pods never render cards anyway, `SeatView` has no card field either). */
export interface WatchedSeat {
  seat: number;
  stack: number;
  folded: boolean;
}

export interface FoldedWalk {
  /** every action after hero folded, in order. */
  actions: WatchedAction[];
  /** the board as it actually ran out. Public information - everyone at the table sees it. */
  board: readonly CardIndex[];
  /** seats that turned their hand over. EMPTY ⇒ the pot was taken without a showdown and nobody's cards
   *  were ever seen. That is the honest live outcome, not a gap in the data. */
  shown: ShownHand[];
  /** the final pot. */
  pot: number;
  /** seats that were awarded chips (a chop awards more than one). */
  winners: number[];
  /** the street hero folded on. */
  foldedOn: Street;
  /** how many players were still live once hero folded. */
  livePlayers: number;
  /** where every seat finished (stacks + who was out) - the felt's aftermath. */
  seats: WatchedSeat[];
}

/** total chips in the middle. Summed from `committedTotal` rather than `state.pots`, because BOTH terminal
 *  paths clear `pots: []` on award - this stays correct through completion. */
const potFromCommitted = (s: HandState): number =>
  s.players.reduce((sum, p) => sum + p.committedTotal, 0);

/** a seat's total equity in the hand: chips in front of him PLUS chips he has pushed in. A bet or call only
 *  moves value between the two, so this is INVARIANT across betting and moves only when the pot is awarded.
 *  That makes `equity(after) - equity(before) > 0` an exact test for "this seat was paid", and it stays
 *  exact for a chopped pot, where a winner can break even on the hand yet still be awarded half. */
const seatEquity = (s: HandState, seat: number): number => {
  const p = s.players.find((pp) => pp.seat === seat);
  return p ? p.stack + p.committedTotal : 0;
};

const lastAggressorOn = (s: HandState, street: Street, exceptSeat: number): number | undefined => {
  for (let i = s.history.length - 1; i >= 0; i--) {
    const h = s.history[i];
    if (h.street !== street) continue;
    const k = h.action.kind;
    if (h.action.seat !== exceptSeat && (k === 'bet' || k === 'raise' || k === 'jam')) return h.action.seat;
  }
  return undefined;
};

const hasAggressionOn = (s: HandState, street: Street): boolean =>
  s.history.some(
    (h) => h.street === street && (h.action.kind === 'bet' || h.action.kind === 'raise' || h.action.kind === 'jam'),
  );

/**
 * Is there anything to watch if hero folds here? CHEAP - a legality check and a head count, no walk.
 *
 * The UI needs this on every render to decide whether to offer the lane, and `buildFoldedWalk` is a full
 * engine re-advance to showdown (the same 0.2–4s of synchronous work a session hand costs). Running that
 * speculatively, per render, to grey out a button would be a real jank source. Same predicate the walk
 * itself opens with, so the two can never disagree: if this says yes, the walk returns a FoldedWalk.
 */
export const canWatchFold = (model: LiveHandModel, from: HandState): boolean => {
  const heroSeat = model.heroSeat;
  const hero = from.players.find((p) => p.seat === heroSeat);
  if (!hero || hero.folded || from.phase !== 'acting') return false;
  if (!legalActions(from).some((l) => l.kind === 'fold')) return false;
  // hero folding leaves the pot to a single player ⇒ nothing to watch, it is just pushed.
  return from.players.filter((p) => !p.folded && p.seat !== heroSeat).length >= 2;
};

/**
 * Fold hero out at `from` and let the table finish the hand.
 *
 * Deterministic in (spec.seed, from) and pure - a fresh rng salted `::tb4-watch`, no API call, no new
 * strategy. Villains run through the SAME `scriptVillainAction` + TP1 preflop policy the live walk uses, so
 * what you watch from the rail is the same table you were just sitting at.
 *
 * Returns null when there is nothing to watch: hero cannot fold here, or hero folding leaves fewer than two
 * players in the pot (the last man standing just takes it - a real outcome, but not an observation).
 */
export const buildFoldedWalk = (model: LiveHandModel, from: HandState): FoldedWalk | null => {
  const heroSeat = model.heroSeat;
  const heroNow = from.players.find((p) => p.seat === heroSeat);
  if (!heroNow || heroNow.folded || from.phase !== 'acting') return null;
  if (!legalActions(from).some((l) => l.kind === 'fold')) return null;

  const spec = model.spec;
  const n = spec.villains.length + 1;
  const deal = dealForSpec(spec, n);
  const rng = createRng(`${spec.seed}::tb4-watch`);
  const policy = sessionVillainPolicy(spec);
  const foldedOn = from.street;

  let state = advance(from, { seat: heroSeat, kind: 'fold' });
  const livePlayers = state.players.filter((p) => !p.folded).length;
  if (livePlayers < 2) return null; // hero's fold ended it - nobody left to watch.

  const actions: WatchedAction[] = [];
  const guard = n * 60;
  for (let k = 0; k < guard; k++) {
    if (state.phase !== 'acting' || state.toAct === null) break;
    const seat = state.toAct;
    if (seat === heroSeat) break; // unreachable (hero is folded); never spin on it.
    const villain = state.players.find((p) => p.seat === seat)!;

    const before = state;
    const street = before.street;
    const priorThisStreet = villain.committedThisStreet;
    const opensStreet = !hasAggressionOn(before, street);
    const vsSeat = lastAggressorOn(before, street, seat);
    const action = scriptVillainAction(before, seat, archetypeOf(villain.archetypeId!), deal, rng, policy);
    state = advance(before, action);

    const isRaise = action.kind === 'bet' || action.kind === 'raise' || action.kind === 'jam';
    const amount = isRaise
      ? (action.amount ?? before.currentBet)
      : action.kind === 'call'
        ? before.currentBet - priorThisStreet
        : 0;
    actions.push({
      seat,
      street,
      kind: action.kind,
      amount,
      potAfter: potFromCommitted(state),
      ...(vsSeat !== undefined ? { vsSeat } : {}),
      opensStreet: opensStreet && isRaise,
      isReraise: isRaise && !opensStreet,
    });
  }

  const winners = state.players
    .filter((p) => seatEquity(state, p.seat) - seatEquity(from, p.seat) > 0)
    .map((p) => p.seat);
  // THE FIREWALL: `holeCards` here comes from the engine's own showdown reveal, so a seat that folded or a
  // pot won without a showdown contributes nothing. Hero is excluded explicitly as well - he is folded, so
  // `!p.folded` already drops him, but his cards are the one pair the state always carries and belt-and-
  // braces is cheap.
  const shown: ShownHand[] = state.players
    .filter((p) => p.seat !== heroSeat && !p.folded && p.holeCards !== undefined)
    .map((p) => ({ seat: p.seat, cards: p.holeCards!, won: winners.includes(p.seat) }));

  return {
    actions,
    board: state.board,
    shown,
    pot: potFromCommitted(state),
    winners,
    foldedOn,
    livePlayers,
    seats: state.players.map((p) => ({ seat: p.seat, stack: p.stack, folded: p.folded })),
  };
};
